import { createFileRoute } from "@tanstack/react-router";
import { PageHeader } from "@/components/page-header";
import { KpiCard } from "@/components/kpi-card";
import { revenueByMonth, topPackages, kpis, fmtUsd } from "@/mocks/data";

export const Route = createFileRoute("/reports")({
  head: () => ({
    meta: [
      { title: "Reports — Studio OS" },
      { name: "description", content: "Revenue, bookings, and top packages." },
    ],
  }),
  component: ReportsPage,
});

function ReportsPage() {
  const max = Math.max(...revenueByMonth.map((m) => m.v));
  const totalYtd = revenueByMonth.reduce((s, m) => s + m.v, 0);
  const totalBookings = topPackages.reduce((s, p) => s + p.bookings, 0);

  return (
    <div className="mx-auto max-w-7xl px-6 py-8 lg:px-10 lg:py-10">
      <PageHeader
        title="Reports"
        description="Studio performance at a glance."
      />
      <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-3">
        <KpiCard label="Revenue YTD" value={fmtUsd(totalYtd)} hint="Last 6 months" />
        <KpiCard label="Bookings YTD" value={String(totalBookings)} hint="Across packages" />
        <KpiCard accent label="Active month" value={fmtUsd(kpis.revenueMtd)} hint={`+${kpis.revenueChangePct}% vs prior`} />
      </div>

      <section className="mb-8 rounded-xl bg-card p-6 ring-1 ring-border">
        <div className="mb-6 flex items-baseline justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Revenue by month
          </h2>
          <span className="font-mono text-[10px] uppercase text-muted-foreground">USD</span>
        </div>
        <div className="flex h-56 items-end gap-4">
          {revenueByMonth.map((m, i) => {
            const h = (m.v / max) * 100;
            const isLast = i === revenueByMonth.length - 1;
            return (
              <div key={m.m} className="flex flex-1 flex-col items-center gap-2">
                <span className="font-mono text-[10px] text-muted-foreground">{fmtUsd(m.v, { compact: true })}</span>
                <div
                  className={`w-full rounded-t-md transition-all ${isLast ? "bg-brand" : "bg-foreground/80"}`}
                  style={{ height: `${h}%` }}
                />
                <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{m.m}</span>
              </div>
            );
          })}
        </div>
      </section>

      <section className="rounded-xl bg-card ring-1 ring-border">
        <div className="border-b border-border px-6 py-4">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Top packages
          </h2>
        </div>
        <div className="divide-y divide-border">
          {topPackages.map((p) => {
            const share = (p.revenue / topPackages.reduce((s, x) => s + x.revenue, 0)) * 100;
            return (
              <div key={p.name} className="px-6 py-4">
                <div className="mb-2 flex items-center justify-between">
                  <div>
                    <p className="font-medium">{p.name}</p>
                    <p className="font-mono text-[11px] uppercase text-muted-foreground">
                      {p.bookings} bookings
                    </p>
                  </div>
                  <p className="font-mono font-medium">{fmtUsd(p.revenue)}</p>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-muted">
                  <div className="h-full rounded-full bg-foreground/70" style={{ width: `${share}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
